import React, { useEffect, useState, useMemo } from 'react';
import { getUserProgress, UserProgress } from '../services/userProgressService';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';

// ==================== COMPONENTES AUXILIARES ====================

// Componente de Card de Estatística
const StatCard: React.FC<{
  icon: string;
  label: string;
  value: string | number;
  subValue?: string;
  color: string;
}> = ({ icon, label, value, subValue, color }) => {
  const colorClasses: Record<string, string> = {
    emerald: 'from-emerald-500 to-teal-500',
    blue: 'from-blue-500 to-indigo-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-amber-500',
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
          <span className="text-2xl">{icon}</span>
        </div>
      </div>
      <p className="text-3xl font-black text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  );
};

// Componente de Gráfico Circular
const CircularProgress: React.FC<{
  percentage: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
}> = ({ percentage, size = 100, strokeWidth = 8, label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-white/20"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="text-white transition-all duration-1000 ease-out"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-white">{percentage}%</span>
        {label && <span className="text-xs font-medium text-white/80">{label}</span>}
      </div>
    </div>
  );
};

// Componente de Heatmap simplificado
const ActivityHeatmap: React.FC<{ activityHistory: Record<string, number> | undefined }> = ({ activityHistory }) => {
  const safeHistory = activityHistory || {};
  
  const weeks = useMemo(() => {
    const result: { date: string; count: number }[][] = [];
    const today = new Date();
    
    for (let w = 11; w >= 0; w--) {
      const week: { date: string; count: number }[] = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(today);
        date.setDate(date.getDate() - (w * 7 + (6 - d)));
        const dateStr = date.toISOString().split('T')[0];
        week.push({
          date: dateStr,
          count: safeHistory[dateStr] || 0,
        });
      }
      result.push(week);
    }
    return result;
  }, [safeHistory]);

  const getIntensityClass = (count: number) => {
    if (count === 0) return 'bg-gray-100';
    if (count <= 5) return 'bg-emerald-200';
    if (count <= 15) return 'bg-emerald-400';
    if (count <= 30) return 'bg-emerald-500';
    return 'bg-emerald-600';
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <span>📅</span> Atividade (3 meses)
        </h3>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <span>Menos</span>
          <div className="w-3 h-3 rounded-sm bg-gray-100"></div>
          <div className="w-3 h-3 rounded-sm bg-emerald-200"></div>
          <div className="w-3 h-3 rounded-sm bg-emerald-400"></div>
          <div className="w-3 h-3 rounded-sm bg-emerald-600"></div>
          <span>Mais</span>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          <div className="flex flex-col gap-1 mr-2 text-xs text-gray-400">
            <span className="h-3"></span>
            <span className="h-3 leading-3">S</span>
            <span className="h-3"></span>
            <span className="h-3 leading-3">Q</span>
            <span className="h-3"></span>
            <span className="h-3 leading-3">S</span>
            <span className="h-3"></span>
          </div>
          
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-1">
              {week.map((day) => (
                <div
                  key={day.date}
                  className={`w-3 h-3 rounded-sm ${getIntensityClass(day.count)} transition-all hover:ring-2 hover:ring-emerald-400 cursor-pointer`}
                  title={`${day.date}: ${day.count} questões`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Componente de Desempenho por Matéria
const SubjectPerformance: React.FC<{ history: any[] | undefined }> = ({ history }) => {
  const safeHistory = history || [];
  
  const subjectStats = useMemo(() => {
    const stats: Record<string, { total: number; correct: number }> = {};
    
    safeHistory.forEach((answer: any) => {
      const subject = (answer?.subject && typeof answer.subject === 'string' && answer.subject.trim() !== '')
        ? answer.subject
        : ((answer?.topic && typeof answer.topic === 'string' && answer.topic.trim() !== '') ? answer.topic : 'Outros');
      
      if (!stats[subject]) {
        stats[subject] = { total: 0, correct: 0 };
      }
      stats[subject].total++;
      if (answer?.isCorrect === true) {
        stats[subject].correct++;
      }
    });

    return Object.entries(stats)
      .map(([subject, data]) => ({
        subject,
        total: data.total,
        correct: data.correct,
        percentage: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8); // Top 8 matérias
  }, [safeHistory]);

  if (subjectStats.length === 0) {
    return null;
  }

  const getStatusIcon = (percentage: number) => {
    if (percentage >= 70) return '🔥';
    if (percentage >= 50) return '⚠️';
    return '❌';
  };

  const getBarColor = (percentage: number) => {
    if (percentage >= 70) return 'from-emerald-400 to-emerald-500';
    if (percentage >= 50) return 'from-amber-400 to-amber-500';
    return 'from-red-400 to-red-500';
  };

  const getTextColor = (percentage: number) => {
    if (percentage >= 70) return 'text-emerald-600';
    if (percentage >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <span>📊</span> Desempenho por Matéria
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="flex items-center gap-1"><span>🔥</span> ≥70%</span>
          <span className="flex items-center gap-1"><span>⚠️</span> 50-69%</span>
          <span className="flex items-center gap-1"><span>❌</span> &lt;50%</span>
        </div>
      </div>
      
      <div className="space-y-4">
        {subjectStats.map((subject, index) => (
          <div key={subject.subject + index}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-700 truncate max-w-[50%]">
                {subject.subject}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {subject.correct}/{subject.total}
                </span>
                <span className={`text-sm font-bold ${getTextColor(subject.percentage)}`}>
                  {subject.percentage}%
                </span>
                <span>{getStatusIcon(subject.percentage)}</span>
              </div>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full bg-gradient-to-r ${getBarColor(subject.percentage)} transition-all duration-500`}
                style={{ width: `${Math.max(subject.percentage, 2)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Legenda de prioridade */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          💡 <strong>Dica:</strong> Foque nas matérias com ⚠️ ou ❌ para melhorar seu aproveitamento geral.
        </p>
      </div>
    </div>
  );
};

// Componente de Badges
const BadgesSection: React.FC<{ badges: any[] | undefined }> = ({ badges }) => {
  const safeBadges = badges || [];
  
  if (safeBadges.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center">
        <span className="text-4xl mb-3 block">🏆</span>
        <h3 className="font-bold text-gray-900 mb-2">Conquistas</h3>
        <p className="text-sm text-gray-500">Continue estudando para desbloquear conquistas!</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100">
      <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
        <span>🏆</span> Suas Conquistas
        <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
          {safeBadges.length}
        </span>
      </h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {safeBadges.map((badge, index) => (
          <div
            key={badge?.id || index}
            className="flex flex-col items-center p-3 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200 hover:shadow-md hover:scale-105 transition-all cursor-pointer"
            title={badge?.description || ''}
          >
            <span className="text-3xl mb-1">{badge?.icon || '🏅'}</span>
            <p className="text-xs font-bold text-gray-800 text-center leading-tight">{badge?.name || 'Badge'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

const UserStats: React.FC = () => {
  const [stats, setStats] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      if (!authChecked) return;
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const data = await getUserProgress();
        setStats(data);
      } catch (error) {
        console.error('[UserStats] Erro:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [authChecked, isAuthenticated]);

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  // ==================== NÃO AUTENTICADO ====================
  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-3xl p-8 text-center border border-gray-100">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-4xl">🔒</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Faça login para ver seu desempenho</h3>
        <p className="text-gray-500">Acompanhe sua evolução e conquiste badges!</p>
      </div>
    );
  }

  // ==================== SEM DADOS ====================
  if (!stats || (stats.totalAnswered || 0) === 0) {
    return (
      <div className="bg-white rounded-3xl p-8 text-center border border-gray-100">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-4xl">📊</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum dado ainda</h3>
        <p className="text-gray-500 mb-4">Responda questões para ver suas estatísticas aqui!</p>
        <div className="inline-flex items-center gap-2 text-emerald-600 font-semibold">
          <span>🚀</span> Comece agora
        </div>
      </div>
    );
  }

  // ==================== CÁLCULOS ====================
  const totalAnswered = stats.totalAnswered || 0;
  const totalCorrect = stats.totalCorrect || 0;
  const accuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  const currentStreak = stats.currentStreak || 0;
  const longestStreak = stats.longestStreak || 0;
  const weeklyProgress = stats.weeklyProgress || 0;
  const weeklyGoal = stats.weeklyGoal || 50;
  const weeklyPercentage = weeklyGoal > 0 ? Math.min(100, Math.round((weeklyProgress / weeklyGoal) * 100)) : 0;

  // ==================== RENDER ====================
  return (
    <div className="space-y-6">
      {/* Header com resumo */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-2xl font-black mb-1">Seu Desempenho</h2>
            <p className="text-emerald-100">
              Você já respondeu <strong className="text-white">{totalAnswered}</strong> questões!
            </p>
          </div>
          <div className="flex items-center gap-6">
            <CircularProgress 
              percentage={accuracy} 
              size={100} 
              label="Precisão"
            />
            <div className="hidden md:block border-l border-white/20 h-16 mx-2"></div>
            <div className="hidden md:flex flex-col items-center">
              <span className="text-4xl font-black">{currentStreak}</span>
              <span className="text-emerald-100 text-sm">🔥 Dias seguidos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="📝"
          label="Respondidas"
          value={totalAnswered}
          color="blue"
        />
        <StatCard
          icon="✅"
          label="Acertos"
          value={totalCorrect}
          subValue={`${accuracy}% de aproveitamento`}
          color="emerald"
        />
        <StatCard
          icon="🔥"
          label="Sequência Atual"
          value={`${currentStreak} dias`}
          subValue={`Recorde: ${longestStreak} dias`}
          color="orange"
        />
        <StatCard
          icon="🎯"
          label="Meta Semanal"
          value={`${weeklyProgress}/${weeklyGoal}`}
          subValue={`${weeklyPercentage}% completo`}
          color="purple"
        />
      </div>

      {/* Meta Semanal */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <span>🎯</span> Meta Semanal
          </h3>
          <span className="text-sm font-medium text-gray-500">
            {weeklyProgress} / {weeklyGoal} questões
          </span>
        </div>
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500 relative"
            style={{ width: `${weeklyPercentage}%` }}
          >
            {weeklyPercentage >= 20 && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-white">
                {weeklyPercentage}%
              </span>
            )}
          </div>
        </div>
        {weeklyPercentage >= 100 && (
          <p className="text-sm text-emerald-600 font-semibold mt-2 flex items-center gap-1">
            <span>🎉</span> Parabéns! Meta alcançada!
          </p>
        )}
      </div>

      {/* Desempenho por Matéria */}
      <SubjectPerformance history={stats.history} />

      {/* Heatmap */}
      <ActivityHeatmap activityHistory={stats.activityHistory} />

      {/* Badges */}
      <BadgesSection badges={stats.badges} />

      {/* Dica personalizada */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200">
        <h3 className="font-bold text-blue-900 flex items-center gap-2 mb-3">
          <span>💡</span> Dica Personalizada
        </h3>
        <p className="text-blue-800">
          {accuracy < 50 && "Seu aproveitamento está abaixo de 50%. Foque em revisar os conceitos básicos antes de avançar."}
          {accuracy >= 50 && accuracy < 70 && "Você está no caminho certo! Continue praticando e revise as questões que errou."}
          {accuracy >= 70 && accuracy < 85 && "Ótimo desempenho! Aumente a dificuldade das questões para evoluir ainda mais."}
          {accuracy >= 85 && "Excelente! Você está pronto para simulados completos. Mantenha a consistência!"}
        </p>
      </div>
    </div>
  );
};

export default UserStats;
